import api from '../../api/appauth';
import * as agreementsApi from '../../api/agreements';
import billingApi from '../../api/billing';
import { router } from '../../main';
import firebase from 'firebase/app';
import 'firebase/auth';
import config from '../../common/config';

/** sessionStorage flag (per tab): the updated-terms popup was already shown in this tab. */
const UPDATED_TERMS_SHOWN_KEY = 'updated_terms_popup_shown';

const state = {
  apptoken: window.localStorage.getItem('travel_app_token'),
  email: window.localStorage.getItem('travel_app_user_email') || '',
  userName: window.localStorage.getItem('travel_app_user_name'),
  password: '',
  rePassword: '',
  isNewUser: false,
  userRole: window.localStorage.getItem('travel_app_user_role') || 'user',
  /**
   * null | 'onboarding' (record + promote) | 'prepaid_share' (record only, then bump nonce for EditRideAccessKey)
   */
  businessBasicAgreementFlow: null,
  /** Incremented when prepaid_share flow records agreement; EditRideAccessKey watches and runs approvePrepaidRideShare. */
  BisnessBasicAgreementShareGateCompletedNonce: 0,
  /** True while prepaid share flow waits for user to accept business basic agreement modal (cleared on dismiss or after approve chain). */
  BisnessBasicAgreementShareGateArmed: false,
  /** null = unknown; true/false = signed ANY business agreement version (billing profile). */
  businessBasicAgreementAnyAccepted: null,
  /** null = unknown; true/false = signed the CURRENT business agreement version (billing profile). */
  businessBasicAgreementLatestAccepted: null,
  /** A valid phone is on file, so the updated-terms popup can sign without asking for one. */
  businessBasicAgreementHasPhone: false,
};

const getters = {
  email: state => state.email,
  userName: state => state.userName,
  password: state => state.password,
  rePassword: state => state.rePassword,
  apptoken: state => state.apptoken,
  isAppLoggedIn: state => !!state.apptoken,
  userId: state => {
    if (!state.apptoken) return null;
    try {
      const tokenData = JSON.parse(atob(state.apptoken.split('.')[1]));
      return tokenData.user_id;
    } catch (error) {
      console.error("Invalid token:", error);
      return null;
    }
  },
  isNewUser: state => state.isNewUser,
  compareToUserId: ( state ) => ( authorId ) => {
    if( ! state.apptoken ){
      return false;
    }
    
    try {
      const tokenData = JSON.parse(atob(state.apptoken.split('.')[1]));
      return tokenData.user_id == authorId;
    } catch (error) {
      console.error("Invalid token in compareToUserId:", error);
      return false;
    }
  },
  userRole: state => state.userRole,
  isUserManager: state => {
    if( state.userRole == 'manager'){
      return true;
    }
    return false
  },
  isUserSubdomainManager: state => {
    if (!state.apptoken) return false;
    try {
      const userId = Number(JSON.parse(atob(state.apptoken.split('.')[1])).user_id);
      return Object.values(config.config.THEMES).some(theme => theme.userId === userId);
    } catch (error) {
      console.error("Invalid token:", error);
      return false;
    }
  },
  businessBasicAgreementFlow: state => state.businessBasicAgreementFlow,
  BisnessBasicAgreementShareGateCompletedNonce: state => state.BisnessBasicAgreementShareGateCompletedNonce,
  BisnessBasicAgreementShareGateArmed: state => state.BisnessBasicAgreementShareGateArmed,
  businessBasicAgreementLatestAccepted: state => state.businessBasicAgreementLatestAccepted,
  businessBasicAgreementHasPhone: state => state.businessBasicAgreementHasPhone,
  /**
   * Re-sign flow: ride managers who signed an OLDER agreement version (incl. the
   * manual_email_v1 backfill) but not the current one. Users who never signed any
   * version are not included -- they keep the original first-time signing flow.
   */
  mustSignUpdatedTerms: (state, getters) =>
    getters.isAppLoggedIn
    && getters.isUserManager
    && state.businessBasicAgreementAnyAccepted === true
    && state.businessBasicAgreementLatestAccepted === false,
};

const actions = {
  resetParameters({commit}) {
    commit('setToken1', null);
    commit('setEmail', '');
    commit('setPassword', '');
    commit('setRePassword', '');
    commit('setIsNewUser', false);
    commit('setUserRole', 'user');
    commit('setBusinessBasicAgreementFlow', null);
    commit('setBisnessBasicAgreementShareGateArmed', false);
    commit('setBusinessBasicAgreementAnyAccepted', null);
    commit('setBusinessBasicAgreementLatestAccepted', null);
    commit('setBusinessBasicAgreementHasPhone', false);
    // A different manager logging in on this tab must still get the popup.
    try {
      window.sessionStorage.removeItem(UPDATED_TERMS_SHOWN_KEY);
    } catch (e) {
      // Storage blocked: nothing to clear.
    }
  },

  /**
   * Ride managers only (called on entering /user_rides): load whether the current
   * agreement version is signed and, if not, open the updated-terms popup -- once per
   * tab (sessionStorage), so it comes back in a new tab or after the tab is closed.
   * The share page gate is not affected and still blocks sharing until signed.
   */
  async checkUpdatedTermsForManager({ state, getters, commit, dispatch, rootGetters }) {
    if (!getters.isAppLoggedIn || !getters.isUserManager) {
      return;
    }
    try {
      const resp = await billingApi.getBillingProfile(state.apptoken);
      const profile = resp?.data || {};
      commit('setBusinessBasicAgreementAnyAccepted', profile.business_basic_agreement_accepted === true);
      commit('setBusinessBasicAgreementLatestAccepted', profile.business_basic_agreement_latest_accepted === true);
      commit('setBusinessBasicAgreementHasPhone', profile.has_phone === true);
    } catch (error) {
      // Unknown state: no popup. The share page re-checks through its own billing profile fetch.
      console.error('Failed to check updated terms:', error);
      return;
    }
    if (!getters.mustSignUpdatedTerms) {
      return;
    }
    // Never close a popup the user is in the middle of. Skip the '' key:
    // showModal('') (closing all) sets modals[''] = true, which is not a real popup.
    const anyModalOpen = Object.entries(rootGetters.modals || {}).some(([key, open]) => key !== '' && open);
    if (anyModalOpen) {
      return;
    }
    let shownThisSession = false;
    try {
      shownThisSession = window.sessionStorage.getItem(UPDATED_TERMS_SHOWN_KEY) === '1';
    } catch (e) {
      // Storage blocked: fall back to showing it on every visit.
    }
    if (shownThisSession) {
      return;
    }
    await dispatch('showModal', 'updatedTerms');
    // showModal is silently ignored while another modal holds the lock: only mark
    // the session as "shown" if the popup really opened, or it would never appear.
    if (!(rootGetters.modals && rootGetters.modals.updatedTerms)) {
      return;
    }
    try {
      window.sessionStorage.setItem(UPDATED_TERMS_SHOWN_KEY, '1');
    } catch (e) {
      // Storage blocked: the popup will just show again next visit.
    }
  },

  /** Updated-terms popup: record the current version. Phone is optional when one is already on file. */
  async acceptUpdatedTerms({ state, commit, dispatch, rootGetters }, payload = {}) {
    const lang = rootGetters['language/getCurrentLanguage'];
    const phone = (payload.phone && String(payload.phone).trim()) || '';
    try {
      const response = await agreementsApi.recordUserAgreement(state.apptoken, {
        agreement_type: agreementsApi.BUSINESS_BASIC_AGREEMENT_TYPE,
        agreement_version: agreementsApi.BUSINESS_BASIC_AGREEMENT_VERSION,
        language: lang === 'he' ? 'he' : 'en',
        source: 'update_terms',
        phone,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
      const code = response.data?.ServerMassageCode;
      if (code && code !== 1 && code !== 'success' && code !== 'secsses') {
        dispatch('showModalWithParameters', { errorNumber: code, type: 'textConverter' });
        return false;
      }
    } catch (error) {
      console.error('Failed to record updated terms:', error);
      const apiCode = error.response && error.response.data && error.response.data.ServerMassageCode;
      dispatch('showModalWithParameters', {
        errorNumber: apiCode || 'record_user_agreement_failed',
        type: 'textConverter',
      });
      return false;
    }
    commit('setBusinessBasicAgreementAnyAccepted', true);
    commit('setBusinessBasicAgreementLatestAccepted', true);
    commit('setBusinessBasicAgreementAccepted', true);
    dispatch('showModal', '');
    return true;
  },

  async registerNewUser({ rootState, commit, dispatch } ){
    const { email, password, userName } = rootState.appauth;
    const response = await api.registerNewUser(email, password, userName);
    if (response.data.ServerMassageCode && (response.data.ServerMassageCode != 1)) {
      dispatch('showModalWithParameters', {
        errorNumber: response.data.ServerMassageCode,
        type: 'textConverter'
      });
      return;
    }
    commit('setUserInformation', { token: response.data.token, role: response.data.role });
    sessionStorage.removeItem('affiliate_referrer_id');
    dispatch('showModalWithParameters', {
      errorNumber: 'userRegisteredSuccessfully',
      textToAdd: email,
      type: 'textConverter'
    });
  },

  async loginToAppWithFaceBook({dispatch}) {
    // at the login with oath we have an issue that the user can log in with one operator( face or google )
    // there is an option to fix it by adding the user other acount to the oath or  removing the dependency of firebase 
    // we can see this information about adding other operator to firebase auutenticated user:
    // https://stackoverflow.com/questions/44015751/firebase-js-api-auth-account-exists-with-different-credential
    var provider = new firebase.auth.FacebookAuthProvider();
    provider.addScope('email');
    try {
      var result = await firebase.auth().signInWithPopup(provider);
    } catch (err) {
      if(err.code == "auth/account-exists-with-different-credential"){
        dispatch( 'showModalWithParameters',
        { errorNumber : "account_exists_with_different_credential",
          type:'textConverter' 
        } );
      }
      return;
    }
    var email = result.user.email;
    if ( email == null ) {
      dispatch( 'showModalWithParameters',
      { errorNumber : "email_is_not_baind_to_this_acount",
        type:'textConverter' 
      } );
      return;
    }
    dispatch('authenticate', {  email: result.user.email,
      facebookAccessToken: result.credential.accessToken })
    dispatch('closeLoginForm');
  },

  async loginToAppWithGoogle({dispatch}) {
    var provider = new firebase.auth.GoogleAuthProvider();
    try {
      var resolt = await firebase.auth().signInWithPopup(provider)
    } catch (err) {
      if(err.code == "auth/account-exists-with-different-credential"){
        dispatch( 'showModalWithParameters',
        { errorNumber : "account_exists_with_different_credential",
          type:'textConverter' 
        } );
      }
      return;
    }
    dispatch('authenticate', {  email: resolt.user.email,
                                googleIdToken: resolt.credential.idToken })
    dispatch('closeLoginForm');
    // TODO We should present error if ther is aproblem!!!!
  },
  openLoginForm({ dispatch }) {
    dispatch( 'showModal', "mainLogIn" );    
  },
  closeLoginForm({ dispatch }) {
    dispatch( 'showModal', "" );    
  },
  // Promote current logged-in user to ride manager on the server
  async makeCurrentUserRideManager({ state, commit, dispatch, rootState }) {
    const token = state.apptoken;
    if (!token) {
      return false;
    }
    try {
      const currency = rootState.regionConfig.currency;
      const response = await api.makeRideManager(token, currency);
      const code = response.data?.ServerMassageCode;
      if (code && code !== 1 && code !== 'success' && code !== 'secsses') {
        dispatch('showModalWithParameters', {
          errorNumber: code,
          type: 'textConverter',
        });
        return false;
      }
      if (response.data && response.data.role) {
        commit('setUserRole', response.data.role);
        return true;
      }
      dispatch('showModalWithParameters', {
        errorNumber: 'make_user_ride_manager_failed',
        type: 'textConverter',
      });
      return false;
    } catch (error) {
      console.error('Failed to promote user to ride manager:', error);
      dispatch('showModalWithParameters', {
        errorNumber: 'make_user_ride_manager_failed',
        type: 'textConverter',
      });
      return false;
    }
  },

  /**
   * Business basic agreement modal completion:
   * - prepaid_share: record agreement (source payment), then bump nonce so EditRideAccessKey runs approvePrepaidRideShare.
   * - onboarding (default): record (signup), then promote to ride manager.
   */
  async acceptBisnessBasicAgreementAndBecomeRideManager({ state, commit, dispatch, rootGetters }, payload = {}) {
    const token = state.apptoken;
    if (!token) {
      return false;
    }
    const flow = state.businessBasicAgreementFlow || 'onboarding';
    const lang = rootGetters['language/getCurrentLanguage'];
    const language = lang === 'he' ? 'he' : 'en';
    const source = flow === 'prepaid_share' ? 'payment' : 'signup';
    const phone = (payload.phone && String(payload.phone).trim()) || '';
    try {
      const response = await agreementsApi.recordUserAgreement(token, {
        agreement_type: agreementsApi.BUSINESS_BASIC_AGREEMENT_TYPE,
        agreement_version: agreementsApi.BUSINESS_BASIC_AGREEMENT_VERSION,
        language,
        source,
        phone,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      });
      const code = response.data?.ServerMassageCode;
      if (code && code !== 1 && code !== 'success' && code !== 'secsses') {
        commit('setBusinessBasicAgreementFlow', null);
        dispatch('showModalWithParameters', {
          errorNumber: code,
          type: 'textConverter',
        });
        return false;
      }
    } catch (error) {
      console.error('Failed to record user agreement:', error);
      commit('setBusinessBasicAgreementFlow', null);
      const apiCode = error.response && error.response.data && error.response.data.ServerMassageCode;
      const errKey = apiCode && apiCode !== 1 && apiCode !== 'success' && apiCode !== 'secsses'
        ? apiCode
        : 'record_user_agreement_failed';
      dispatch('showModalWithParameters', {
        errorNumber: errKey,
        type: 'textConverter',
      });
      return false;
    }

    commit('setBusinessBasicAgreementAnyAccepted', true);
    commit('setBusinessBasicAgreementLatestAccepted', true);

    if (flow === 'prepaid_share') {
      commit('setBusinessBasicAgreementFlow', null);
      commit('setBusinessBasicAgreementAccepted', true);
      commit('bumpBisnessBasicAgreementShareGateCompletedNonce');
      dispatch('showModal', '');
      return true;
    }

    const promoted = await dispatch('makeCurrentUserRideManager');
    if (!promoted) {
      commit('setBusinessBasicAgreementFlow', null);
      return false;
    }

    commit('setBusinessBasicAgreementFlow', null);
    dispatch('showModal', '');
    return true;
  },
  async appLogOut({ commit, dispatch } ) {
    commit('setUserInformation', { token: null, role: 'user' });
    window.localStorage.removeItem('travel_app_token');
    dispatch('resetParameters');
    firebase.auth().signOut()
  },
  equalToUserId( { getters, state }, authorId ){
    if( ! state.apptoken ){
      return false;
    }
    const userId = getters.userId;
    if( userId == authorId ){
      return true;
    }
    return false
  },
  async authenticate({ rootState, commit, dispatch }, userAuthInformation={} ) {
    var response
    var userEmail
    if ( 'googleIdToken' in userAuthInformation ){
      userEmail = userAuthInformation.email
      response = await api.authenticate( userAuthInformation.email, 0, userAuthInformation.googleIdToken, "google" );
    }
    else if ( 'facebookAccessToken' in userAuthInformation ){
      userEmail = userAuthInformation.email
      response = await api.authenticate( userAuthInformation.email, 0, userAuthInformation.facebookAccessToken, "face-book" );
    }
    else {
      const { email } = rootState.appauth;
      const { password } = rootState.appauth;
      userEmail = rootState.appauth.email
      response = await api.authenticate( email, password);
    }
    if( response.data.ServerMassageCode && response.data.ServerMassageCode != 1 && response.data.ServerMassageCode != 'secsses' )
    {
      dispatch( 'showModalWithParameters',
                { errorNumber : response.data.ServerMassageCode,
                  type:'textConverter' 
                } );
      return;          
    }
    commit('setUserInformation', { token: response.data.token, role: response.data.role });
    commit('setEmail', userEmail);
    commit('setUserName', response.data.userName);
    sessionStorage.removeItem('affiliate_referrer_id');
    dispatch( 'showModalWithParameters',
      { errorNumber : "userLoggedInSuccessfully",
        textToAdd : userEmail,
        type:'textConverter'
      } );
    // TODO We should popup this masssege if  aetToke is False
    // and we should close the popup window at this page
    // console.log( response.data.message )
    // commit('setToken1', response.data.token);
  },
  validateAndupdateAppToken: ({ state, dispatch }) =>{
    const jwt = state.apptoken;
    if (!jwt || jwt.split('.').length < 3) { 
      dispatch('appLogOut');
      return;
    }
    
    try {
      const data = JSON.parse(atob(jwt.split('.')[1]));
      
      // Check if token has expiration
      if (!data.exp) {
        console.warn("Token missing expiration claim");
        dispatch('appLogOut');
        return;
      }
      
      const exp = new Date(data.exp * 1000) // JS deals with dates in milliseconds since epoch
      const now = new Date()
      if (now > exp){
        dispatch('appLogOut');  
      }
    } catch (error) {
      console.error("Error validating token:", error);
      dispatch('appLogOut');
    }
  },
  goToTermsOfUse({dispatch}){
    dispatch( 'showModal', '' );    
    router.push('/terms_of_use')
  },

  async requestPasswordReset({ dispatch }, email) {
    try {
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : null;
      const response = await api.requestPasswordReset(email, baseUrl);
      if (response.data.message && response.data.message !== 1 && response.data.message !== 'success') {
        // Instead of throwing error, return false to indicate failure
        dispatch('showModalWithParameters', {
          errorNumber: response.data.message,
          type: 'textConverter'
        });
        return false;
      }
      return true; // Return true to indicate success
    } catch (error) {
      console.error('Password reset request failed:', error);
      dispatch('showModalWithParameters', {
        errorNumber: 'password_reset_failed',
        type: 'textConverter'
      });
      return false;
    }
  },

  async resetPassword({ dispatch }, { token, newPassword }) {
    try {
      const response = await api.resetPassword(token, newPassword);
      if (response.data.message && response.data.message !== 1 && response.data.message !== 'success') {
        dispatch('showModalWithParameters', {
          errorNumber: response.data.message,
          type: 'textConverter'
        });
        return false;
      }
      // Show success message
      dispatch('showModalWithParameters', {
        errorNumber: 'password_reset_success',
        type: 'textConverter'
      });
      return true;
    } catch (error) {
      console.error('Password reset failed:', error);
      dispatch('showModalWithParameters', {
        errorNumber: 'password_reset_failed',
        type: 'textConverter'
      });
      return false;
    }
  },
  
  // Check if user needs to re-login due to update
  checkForUpdate({ dispatch, state }) {
    if (!state.apptoken) return;
    
    try {
      const tokenData = JSON.parse(atob(state.apptoken.split('.')[1]));
      
      // If token doesn't have user_id field, it's old format - force re-login
      if (!tokenData.user_id) {
        dispatch('showModal', 'updateNotification');
      }
    } catch (error) {
      console.error('Error checking for update:', error);
      // If we can't parse the token, force re-login
      dispatch('showModal', 'updateNotification');
    }
  }
};

const mutations = {
  setUserInformation: (state, { token, role }) => {
    mutations.setToken1(state, token);
    mutations.setUserRole(state, role);
  },
  setToken1: (state, token) => {
    state.apptoken = token;
    if( token ){
      window.localStorage.setItem('travel_app_token', token);
    }
    else{
      window.localStorage.removeItem('travel_app_token');
    }
  },
  setEmail: (state, email) => {
    state.email = email;
    if( email ){
      window.localStorage.setItem('travel_app_user_email', email);
    }
    else{
      window.localStorage.removeItem('travel_app_user_email');
    }
  },
  setUserName: (state, userName) => {
    state.userName = userName;
    if( userName ){
      window.localStorage.setItem('travel_app_user_name', userName);
    }
    else{
      window.localStorage.removeItem('travel_app_user_name');
    }
  },
  setPassword: (state, password) => {
    state.password = password;
  },
  setRePassword: (state, rePassword) => {
    state.rePassword = rePassword;
  },
  setIsNewUser: (state, isNewUser) => {
    state.isNewUser = isNewUser;
  },
  setUserRole: ( state, userRole) => {
    state.userRole = userRole;
    if( userRole ){
      window.localStorage.setItem('travel_app_user_role', userRole);
    }
    else{
      window.localStorage.removeItem('travel_app_user_role');
    }
  },
  setBusinessBasicAgreementFlow: (state, payload) => {
    state.businessBasicAgreementFlow = payload;
  },
  bumpBisnessBasicAgreementShareGateCompletedNonce: (state) => {
    state.BisnessBasicAgreementShareGateCompletedNonce += 1;
  },
  setBusinessBasicAgreementAnyAccepted: (state, value) => {
    state.businessBasicAgreementAnyAccepted = value;
  },
  setBusinessBasicAgreementLatestAccepted: (state, value) => {
    state.businessBasicAgreementLatestAccepted = value;
  },
  setBusinessBasicAgreementHasPhone: (state, value) => {
    state.businessBasicAgreementHasPhone = !!value;
  },
  setBisnessBasicAgreementShareGateArmed: (state, value) => {
    state.BisnessBasicAgreementShareGateArmed = !!value;
  },
};

export default {
  state,
  getters,
  actions,
  mutations
};
